import { apiFetch } from './apiClient';

// Known-benign browser noise that isn't worth an admin's attention -- filtered
// out so Operational Logs stays signal, not spam.
const IGNORED_MESSAGES = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Script error.',
];

let installed = false;

function shouldIgnore(message: string): boolean {
  return IGNORED_MESSAGES.some((ignored) => message.includes(ignored));
}

/** Fire-and-forget report of a frontend error into the backend's Operational
 *  Logs ring buffer (see routes/logs.ts `POST /logs/client-error`). Never
 *  throws -- a broken error reporter must not create more errors. */
export function reportClientError(
  kind: 'uncaught' | 'unhandledrejection' | 'react-boundary',
  message: string,
  extra?: { stack?: string; componentStack?: string },
): void {
  if (!message || shouldIgnore(message)) return;
  void apiFetch('/logs/client-error', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      kind,
      message,
      stack: extra?.stack,
      componentStack: extra?.componentStack,
      url: window.location.href,
    }),
  }).catch(() => {
    // Reporting must never surface its own failure to the user.
  });
}

/** Installs window-level handlers for uncaught exceptions and unhandled
 *  promise rejections. Call once at app bootstrap. React render errors are
 *  NOT caught here -- those go through <ErrorBoundary> instead, since React
 *  swallows them before they reach window.onerror. */
export function installGlobalErrorReporting(): void {
  if (installed) return;
  installed = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    const message = event.error instanceof Error ? event.error.message : event.message;
    const stack = event.error instanceof Error ? event.error.stack : undefined;
    reportClientError('uncaught', message || 'Uncaught error', { stack });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason ?? 'Unhandled promise rejection');
    const stack = reason instanceof Error ? reason.stack : undefined;
    reportClientError('unhandledrejection', message, { stack });
  });
}
